import React from 'react';
import './motion.css';
import { Link, useLocation } from 'react-router-dom';
import { panelTo } from './panelLink';
import { ReactComponent as ChevronIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-bottom.svg';
import { chipsFor, cn, isNewRequestFor } from './model/derive';
import { formatDue, tzShortName } from './model/time';
import { dueInfo, useWorkboardCtx } from './model/useWorkboard';
import { StatusChip, UnreadDot } from './primitives';
import PersonChip from './PersonChip';
import { YouLabel } from './TaskCard';

/**
 * List 레이아웃 — Board 열을 같은 순서의 섹션으로. 섹션 머리를 누르면 접힌다(접힘 기본값은 Board 열과 같다).
 * 열: Task | Status | Requested by(My tasks) · Assignee(All tasks) | Due
 */

/** 섹션 색 — 점(10px 사각)과 머리줄 배경(같은 색 6%) */
const SECTION_STYLE = {
  to_accept: { dot: 'bg-grayscale-gray05', head: 'bg-grayscale-gray05/[0.06]' },
  todo: { dot: 'bg-grayscale-gray05', head: 'bg-grayscale-gray05/[0.06]' },
  in_progress: { dot: 'bg-orange-orange04', head: 'bg-orange-orange04/[0.06]' },
  waiting: { dot: 'bg-functional-accent03', head: 'bg-functional-accent03/[0.06]' },
  to_review: { dot: 'bg-functional-accent03', head: 'bg-functional-accent03/[0.06]' },
  in_review: { dot: 'bg-functional-accent03', head: 'bg-functional-accent03/[0.06]' },
  sent_back: { dot: 'bg-functional-warning03', head: 'bg-functional-warning03/[0.06]' },
  done: { dot: 'bg-functional-success03', head: 'bg-functional-success03/[0.06]' },
  approved: { dot: 'bg-functional-success03', head: 'bg-functional-success03/[0.06]' },
};

const COLS = 'grid grid-cols-[minmax(0,1fr)_200px_170px_150px] gap-4';
const RULE = 'border-t border-green-deep03/[0.08]';

const Row = ({ task, index, people, you, labels }) => {
  // 섹션을 펼치면 행이 위에서부터 차례로 떠오른다
  const stagger = { animationDelay: `${Math.min(index, 12) * 25}ms` };
  const ctx = useWorkboardCtx();
  const { adjusted } = dueInfo(ctx, task);
  const tz = tzShortName(ctx.displayTz, ctx.now);
  const isNew = isNewRequestFor(task, ctx.data.viewerId);
  const location = useLocation();
  const to = panelTo(location, task.id);
  const chips = chipsFor(task, ctx.data.viewerId, ctx, labels).filter(
    (c) => c.label !== 'New request',
  );
  const due = adjusted ? (
    <span className='flex flex-col gap-0.5'>
      <span className='font-medium text-functional-warning04'>
        Adj. {formatDue(adjusted, ctx.displayTz)} {tz}
      </span>
      <span className='text-[12px] text-text-secondary'>
        Original {formatDue(task.dueAt, ctx.displayTz)} {tz}
      </span>
    </span>
  ) : (
    <span>
      {formatDue(task.dueAt, ctx.displayTz)} {tz}
    </span>
  );
  const title = (
    <span className='flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1'>
      <span className={cn('text-[14px] leading-[140%]', isNew ? 'font-medium' : 'font-normal')}>
        {task.title}
      </span>
      {isNew && !task.openedAt && <UnreadDot />}
      {you && <YouLabel />}
    </span>
  );
  const rowClass = cn(
    RULE,
    'td-rise relative text-text-primary transition-colors hover:bg-grayscale-gray01',
    // 나와 관련된 행 — 1.5px 안쪽 테두리 (All tasks)
    you && 'shadow-[inset_0_0_0_1.5px_#1dc37b]',
  );
  const newBar = isNew && (
    <span
      aria-hidden
      className='absolute bottom-2.5 left-0 top-2.5 w-[3px] rounded-r-[3px] bg-green-deep02'
    />
  );

  return (
    <>
      {/* 모바일 — 두 줄: 제목 · 칩 / 사람 · 마감 */}
      <Link
        to={to}
        data-coach='task-card'
        style={stagger}
        className={cn(rowClass, 'flex flex-col gap-1.5 px-4 py-3 tabletH:hidden')}
      >
        {newBar}
        {title}
        <span className='flex flex-wrap gap-1'>
          {isNew && <StatusChip chip={{ label: 'New request', tone: 'newRequest' }} />}
          {chips.map((c) => (
            <StatusChip key={c.label} chip={c} />
          ))}
        </span>
        <span className='flex flex-wrap items-center justify-between gap-2 text-[12px] text-text-secondary'>
          {people.length > 0 && <PersonChip members={people} />}
          {due}
        </span>
      </Link>
      {/* 데스크톱 · 태블릿 — 표 */}
      <Link
        to={to}
        data-coach='task-card'
        style={stagger}
        className={cn(
          COLS,
          rowClass,
          'hidden min-h-12 items-center px-4 py-2 text-[13px] leading-[140%] tabletH:grid',
        )}
      >
        {newBar}
        <span className='flex min-w-0 flex-col items-start gap-1'>
          {title}
          {isNew && <StatusChip chip={{ label: 'New request', tone: 'newRequest' }} />}
        </span>
        <span className='flex min-w-0 flex-col items-start gap-1'>
          {chips.map((c) => (
            <StatusChip key={c.label} chip={c} />
          ))}
        </span>
        <span className='min-w-0'>{people.length > 0 && <PersonChip members={people} />}</span>
        <span className='min-w-0 [overflow-wrap:anywhere]'>{due}</span>
      </Link>
    </>
  );
};

const Section = ({ section, personHeading, renderProps }) => {
  const { key, label, head, tasks, folded, onToggle } = section;
  const style = SECTION_STYLE[key];
  const open = !folded;
  return (
    <section className='overflow-hidden rounded-xl border border-green-deep03/10 bg-grayscale-gray00'>
      <button
        type='button'
        onClick={onToggle}
        aria-expanded={open}
        className={cn(
          'flex h-11 w-full select-none items-center gap-2.5 px-3.5 text-left transition-colors',
          style?.head ?? 'bg-green-deep03/[0.03]',
        )}
      >
        <ChevronIcon
          className={cn(
            'h-4 w-4 shrink-0 text-icon-secondary transition-transform duration-300',
            !open && '-rotate-90',
          )}
        />
        {head ??
          (style && <span className={cn('h-2.5 w-2.5 shrink-0 rounded-[3px]', style.dot)} />)}
        <span className='text-[14px] font-medium leading-[140%] text-text-primary'>{label}</span>
        <span className='text-[13px] text-text-secondary'>{tasks.length}</span>
      </button>
      {/* 접고 펼 때 높이가 부드럽게 바뀐다 — 닫혀 있는 동안은 탭·화면 낭독기에서 빠진다 */}
      <div className='td-collapse' data-open={open} aria-hidden={!open} inert={!open || undefined}>
        <div>
          {tasks.length === 0 ? (
            <p className={cn(RULE, 'px-4 py-5 text-[13px] text-text-placeholder')}>Nothing here</p>
          ) : (
            <>
              <div
                className={cn(
                  COLS,
                  RULE,
                  'hidden px-4 py-2.5 text-[12px] font-medium leading-[140%] text-text-secondary tabletH:grid',
                )}
              >
                <span>Task</span>
                <span>Status</span>
                <span>{personHeading}</span>
                <span>Due</span>
              </div>
              {tasks.map((t, i) => (
                <Row key={t.id} task={t} index={i} {...renderProps(t)} />
              ))}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

/**
 * @param sections [{ key, label, head?, tasks, folded, onToggle }]
 * @param renderProps (task) => { people, you, labels }
 */
const ListView = ({ sections, personHeading, renderProps }) => (
  <div className='flex flex-col gap-3'>
    {sections.map((s) => (
      <Section key={s.key} section={s} personHeading={personHeading} renderProps={renderProps} />
    ))}
  </div>
);

export default ListView;
